import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CreditCard,
  Calendar,
  Download,
  FileText,
  Loader2,
  Plus,
  Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { TablePagination } from '@/components/TablePagination';
import { format } from 'date-fns';

import { API_URL } from '@/lib/api';
import { downloadOrderReceipt } from '@/lib/receipt';

const Payments = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    store_id: 'all',
    payment_method: 'all',
    payment_status: 'all',
    start_date: null,
    end_date: null
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [receiptLoadingId, setReceiptLoadingId] = useState(null);
  const [invoiceGenId, setInvoiceGenId] = useState(null);
  
  // Manual Payment Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [manualPayment, setManualPayment] = useState({
    storeId: '',
    amount: '',
    reference: '',
    note: ''
  });

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const mapPayment = (p) => ({
    id: p.id,
    payment_id: p.id,
    store_name: p.storeName ?? p.store_name ?? '—',
    order_id: p.orderId ?? p.order_id ?? null,
    order_number: p.orderNumber ?? p.order_number ?? null,
    invoice_url: p.invoiceUrl ?? p.invoice_url ?? null,
    amount: Number(p.amount) || 0,
    payment_method: String(p.paymentType ?? p.payment_type ?? '').toLowerCase(),
    transaction_id: p.merchantOrderId ?? p.merchant_order_id,
    status: String(p.status ?? '').toLowerCase(),
    created_at: p.createdAt ?? p.created_at,
  });

  const mergeStoresFromRows = (apiStores, paymentRows) => {
    const byId = new Map(
      (apiStores || []).map((s) => [s.id, { id: s.id, store_name: s.storeName || `Store ${String(s.id).slice(0, 8)}…` }])
    );
    (paymentRows || []).forEach((p) => {
      if (p.storeId && !byId.has(p.storeId)) {
        byId.set(p.storeId, {
          id: p.storeId,
          store_name: p.storeName || `Store ${String(p.storeId).slice(0, 8)}…`,
        });
      }
    });
    return Array.from(byId.values()).sort((a, b) =>
      (a.store_name || '').localeCompare(b.store_name || '')
    );
  };

  const fetchStoresAndPayments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.store_id && filters.store_id !== 'all') params.append('storeId', filters.store_id);
      if (filters.payment_method && filters.payment_method !== 'all') {
        params.append('paymentMethod', filters.payment_method === 'online' ? 'ONLINE' : filters.payment_method.toUpperCase());
      }
      if (filters.payment_status && filters.payment_status !== 'all') {
        params.append('status', filters.payment_status.toUpperCase());
      }
      if (filters.start_date) params.append('startDate', filters.start_date.toISOString());
      if (filters.end_date) params.append('endDate', filters.end_date.toISOString());

      const [storeRes, payRes] = await Promise.all([
        axios.get(`${API_URL}/stores`),
        axios.get(`${API_URL}/payments?${params}`),
      ]);

      const storeRows = Array.isArray(storeRes.data) ? storeRes.data : [];
      const raw = payRes.data;
      const rows = Array.isArray(raw) ? raw : [];

      setStores(mergeStoresFromRows(storeRows, rows));
      setPayments(rows.map(mapPayment));
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStoresAndPayments();
  }, [filters]);

  const handleDownloadReceipt = async (payment, e) => {
    e?.preventDefault?.();
    if (!payment.order_id) return;
    setReceiptLoadingId(payment.id);
    try {
      const r = await downloadOrderReceipt(
        payment.order_id,
        payment.order_number || payment.order_id,
        { tryGenerateOnFailure: true }
      );
      toast.success(
        r?.mode === 'hosted' ? 'Invoice PDF opened in a new tab' : 'Receipt downloaded'
      );
    } catch (err) {
      toast.error(err?.message || 'Failed to download receipt');
    } finally {
      setReceiptLoadingId(null);
    }
  };

  const handleGenerateInvoice = async (payment, e) => {
    e?.preventDefault?.();
    if (!payment.order_id) return;
    setInvoiceGenId(payment.id);
    try {
      const { data } = await axios.post(`${API_URL}/orders/${payment.order_id}/generate-invoice`);
      const invUrl = data?.invoiceUrl ?? data?.invoice_url;
      if (invUrl) {
        toast.success('Invoice generated');
        window.open(invUrl, '_blank', 'noopener,noreferrer');
      } else {
        toast.success('Invoice generated');
      }
      fetchStoresAndPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setInvoiceGenId(null);
    }
  };

  const handleManualPaymentSubmit = async (e) => {
    e.preventDefault();
    if (!manualPayment.storeId || !manualPayment.amount) {
      toast.error('Store and Amount are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/payments`, {
        storeId: manualPayment.storeId,
        amount: Number(manualPayment.amount),
        reference: manualPayment.reference || undefined,
        note: manualPayment.note || undefined,
      });
      toast.success(`₹${Number(manualPayment.amount).toLocaleString()} recorded — outstanding balance updated.`);
      setIsModalOpen(false);
      setManualPayment({ storeId: '', amount: '', reference: '', note: '' });
      fetchStoresAndPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record manual payment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="payments-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Payment History</h1>
          <p className="text-slate-500 mt-1">View all payment transactions</p>
        </div>
        <Button 
          onClick={() => setIsModalOpen(true)}
          className="bg-purple-950 hover:bg-purple-900 text-white gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Manual Payment
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
        <Select 
          value={filters.store_id} 
          onValueChange={(value) => setFilters(prev => ({ ...prev, store_id: value }))}
        >
          <SelectTrigger data-testid="store-filter">
            <SelectValue placeholder="All Stores" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stores</SelectItem>
            {stores.map((store) => (
              <SelectItem key={store.id} value={store.id}>{store.store_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select 
          value={filters.payment_method} 
          onValueChange={(value) => setFilters(prev => ({ ...prev, payment_method: value }))}
        >
          <SelectTrigger data-testid="method-filter">
            <SelectValue placeholder="Payment Method" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Methods</SelectItem>
            <SelectItem value="online">Gateway (order / wallet)</SelectItem>
            <SelectItem value="ORDER">Order checkout</SelectItem>
            <SelectItem value="WALLET">Wallet top-up</SelectItem>
            <SelectItem value="COD">COD orders</SelectItem>
            <SelectItem value="MANUAL">Manual Payment</SelectItem>
            <SelectItem value="ADJUSTMENT">Wallet Adjustment</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.payment_status}
          onValueChange={(value) => setFilters((prev) => ({ ...prev, payment_status: value }))}
        >
          <SelectTrigger data-testid="payment-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="success">Success / Paid</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="cod">COD</SelectItem>
            <SelectItem value="adjustment">Adjustment</SelectItem>
          </SelectContent>
        </Select>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="justify-start text-left font-normal" data-testid="start-date-picker">
              <Calendar className="mr-2 h-4 w-4" />
              {filters.start_date ? format(filters.start_date, 'PP') : 'Start Date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={filters.start_date}
              onSelect={(date) => setFilters(prev => ({ ...prev, start_date: date }))}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="justify-start text-left font-normal" data-testid="end-date-picker">
              <Calendar className="mr-2 h-4 w-4" />
              {filters.end_date ? format(filters.end_date, 'PP') : 'End Date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={filters.end_date}
              onSelect={(date) => setFilters(prev => ({ ...prev, end_date: date }))}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-950"></div>
          </div>
        ) : payments.length === 0 ? (
          <div className="empty-state">
            <CreditCard className="w-12 h-12 mb-3 text-slate-300" />
            <p className="text-lg font-medium">No payments found</p>
            <p className="text-sm mt-1">Payment records will appear here</p>
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
          <Table className="min-w-[1080px]">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Payment ID</TableHead>
                <TableHead>Store</TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.slice((page - 1) * pageSize, page * pageSize).map((payment) => (
                <TableRow key={payment.id} className="table-row-hover" data-testid={`payment-row-${payment.id}`}>
                  <TableCell className="font-mono text-sm">{payment.payment_id}</TableCell>
                  <TableCell className="font-medium">{payment.store_name}</TableCell>
                  <TableCell className="font-mono text-sm text-slate-500">
                    {payment.order_id ? (
                      <Link
                        to={`/orders/${payment.order_id}`}
                        className="text-purple-700 hover:underline"
                      >
                        {payment.order_number || `${payment.order_id.slice(0, 8)}…`}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="font-medium">₹{payment.amount.toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="uppercase text-xs">
                      {payment.payment_method}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={
                        ['success', 'paid'].includes(payment.status)
                          ? 'status-success'
                          : payment.status === 'pending'
                            ? 'status-pending'
                            : payment.status === 'failed'
                              ? 'bg-red-100 text-red-800 border-red-200'
                              : payment.status === 'adjustment'
                                ? 'bg-blue-100 text-blue-800 border-blue-200'
                                : 'status-cod'
                      }
                    >
                      {payment.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-500 whitespace-nowrap">
                    {payment.created_at ? new Date(payment.created_at).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {payment.order_id && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 gap-1"
                            disabled={receiptLoadingId === payment.id}
                            onClick={(e) => handleDownloadReceipt(payment, e)}
                            title="Download Receipt"
                          >
                            {receiptLoadingId === payment.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Download className="h-3.5 w-3.5" />
                            )}
                          </Button>
                          {payment.invoice_url ? (
                            <Button variant="outline" size="sm" className="h-8 gap-1" asChild title="View Invoice">
                              <a href={payment.invoice_url} target="_blank" rel="noopener noreferrer">
                                <FileText className="h-3.5 w-3.5" />
                              </a>
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 gap-1"
                              disabled={invoiceGenId === payment.id}
                              onClick={(e) => handleGenerateInvoice(payment, e)}
                              title="Generate Invoice"
                            >
                              {invoiceGenId === payment.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <FileText className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" className="h-8" onClick={() => navigate(`/orders/${payment.order_id}`)} title="View Order">
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <TablePagination
            data={payments}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
          />
          </>
        )}
      </div>

      {/* Manual Payment Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Manual Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleManualPaymentSubmit} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="storeId">Select Store *</Label>
              <Select 
                value={manualPayment.storeId} 
                onValueChange={(val) => setManualPayment(prev => ({ ...prev, storeId: val }))}
              >
                <SelectTrigger id="storeId">
                  <SelectValue placeholder="Choose a store" />
                </SelectTrigger>
                <SelectContent>
                  {stores.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.store_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount">Amount Received (₹) *</Label>
              <Input 
                id="amount" 
                type="number" 
                step="0.01"
                min="1"
                placeholder="0.00"
                value={manualPayment.amount}
                onChange={(e) => setManualPayment(prev => ({ ...prev, amount: e.target.value }))}
              />
              <p className="text-xs text-slate-500">This amount will be deducted from the store's total outstanding balance.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reference">Reference (optional)</Label>
              <Input 
                id="reference"
                placeholder="Bank ref no. / UTR / Cheque no."
                value={manualPayment.reference}
                onChange={(e) => setManualPayment(prev => ({ ...prev, reference: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="note">Note (optional)</Label>
              <Textarea 
                id="note" 
                placeholder="e.g. Cash collected on 16th April"
                value={manualPayment.note}
                onChange={(e) => setManualPayment(prev => ({ ...prev, note: e.target.value }))}
              />
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Record Payment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payments;
